import { useCallback, useEffect, useState } from 'react';
import {
  apiGetTeams,
  apiCreateTeam,
  apiDeleteTeam,
  apiAddTeamMember,
  apiRemoveTeamMember,
} from '../services/api';

// Clé de l'ancien stockage navigateur. Les équipes vivaient là avant d'exister en
// base : on la relit une fois pour les remonter côté serveur, puis on l'efface.
const legacyKey = (coachId) => `coach-teams-${coachId}`;

// Le reste de l'app (Coach.js, CoachTeams.js) manipule `{ id, name, color,
// memberIds }`. On conserve cette forme et on traduit ici, plutôt que de
// répercuter le vocabulaire de l'API dans les composants.
const fromApi = (t) => ({
  id: String(t.id),
  name: t.nom,
  color: t.couleur,
  memberIds: t.membres || [],
});

function readLegacy(coachId) {
  try {
    const raw = localStorage.getItem(legacyKey(coachId));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export default function useTeams(coachId, token) {
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState(null);

  // Remonte les équipes du navigateur vers le serveur, une seule fois. Sans ça,
  // un coach qui avait composé ses groupes les verrait disparaître le jour de la
  // mise en service.
  const migrateLegacy = useCallback(async () => {
    const legacy = readLegacy(coachId);
    if (legacy.length === 0) {
      localStorage.removeItem(legacyKey(coachId));
      return [];
    }

    const created = [];
    for (const old of legacy) {
      try {
        let team = fromApi(await apiCreateTeam(
          { nom: old.name, couleur: old.color }, token,
        ));
        for (const sid of old.memberIds || []) {
          // Une sportive dont le suivi a pris fin depuis est refusée par le
          // serveur : on l'ignore plutôt que d'abandonner toute la reprise.
          try {
            team = fromApi(await apiAddTeamMember(team.id, sid, token));
          } catch { /* membre non repris */ }
        }
        created.push(team);
      } catch { /* équipe non reprise (nom en doublon, par exemple) */ }
    }

    // Effacée seulement après coup : une reprise interrompue sera retentée au
    // prochain chargement plutôt que perdue.
    localStorage.removeItem(legacyKey(coachId));
    return created;
  }, [coachId, token]);

  useEffect(() => {
    if (!coachId || !token) return;
    let cancelled = false;

    (async () => {
      try {
        const remote = await apiGetTeams(token);
        if (cancelled) return;
        if (remote.length === 0) {
          const migrated = await migrateLegacy();
          if (!cancelled) setTeams(migrated);
        } else {
          setTeams(remote.map(fromApi));
        }
        if (!cancelled) setError(null);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [coachId, token, migrateLegacy]);

  // Chaque action renvoie l'état serveur et remplace la ligne concernée : pas de
  // mise à jour optimiste, l'affichage ne peut donc pas diverger de la base.
  const replace = (team) =>
    setTeams((prev) => prev.map((t) => (t.id === team.id ? team : t)));

  const createTeam = async (name, color) => {
    try {
      const created = fromApi(await apiCreateTeam({ nom: name, couleur: color }, token));
      setTeams((prev) => [...prev, created]);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteTeam = async (teamId) => {
    try {
      await apiDeleteTeam(teamId, token);
      setTeams((prev) => prev.filter((t) => t.id !== teamId));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const addMember = async (teamId, sportiveId) => {
    try {
      replace(fromApi(await apiAddTeamMember(teamId, sportiveId, token)));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const removeMember = async (teamId, sportiveId) => {
    try {
      replace(fromApi(await apiRemoveTeamMember(teamId, sportiveId, token)));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const getTeamsForMember = (sportiveId) =>
    teams.filter((t) => t.memberIds.includes(sportiveId));

  return { teams, error, createTeam, deleteTeam, addMember, removeMember, getTeamsForMember };
}
